import type { CampaignEvaluationResponse, RuntimeCampaign } from './types';

export interface ResolvedCampaignSet {
  bannerCampaign: RuntimeCampaign | null;
  overlayCampaign: RuntimeCampaign | null;
}

export interface BannerPlacementSettings {
  position?: string | null;
  mobileElementId?: string | null;
  desktopElementId?: string | null;
}

export interface ActiveFunnelExperience {
  experienceType: string | null;
  bannerPlacement?: BannerPlacementSettings | null;
}

function campaignPriorityValue(campaign: RuntimeCampaign): number {
  return Number.isFinite(campaign.priority) ? campaign.priority : 0;
}

function overlayTypeRank(campaign: RuntimeCampaign): number {
  switch (campaign.campaignType) {
    case 'freegift_popup':
      return 2;
    case 'leads_popup':
      return 1;
    default:
      return 0;
  }
}

function compareOverlayCampaigns(a: RuntimeCampaign, b: RuntimeCampaign): number {
  const priorityDiff = campaignPriorityValue(b) - campaignPriorityValue(a);
  if (priorityDiff !== 0) return priorityDiff;
  return overlayTypeRank(b) - overlayTypeRank(a);
}

function normalizePlacement(input: unknown): BannerPlacementSettings | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const record = input as Record<string, unknown>;
  const position = typeof record.position === 'string' ? record.position : null;
  const mobileElementId = typeof record.mobileElementId === 'string' ? record.mobileElementId : null;
  const desktopElementId = typeof record.desktopElementId === 'string' ? record.desktopElementId : null;

  if (!position && !mobileElementId && !desktopElementId) return null;

  return {
    position,
    mobileElementId,
    desktopElementId,
  };
}

function slotsCollide(a: BannerPlacementSettings | null, b: BannerPlacementSettings | null): boolean {
  if (!a || !b) return false;
  if (a.position && b.position && a.position === b.position) return true;
  if (a.mobileElementId && b.mobileElementId && a.mobileElementId === b.mobileElementId) return true;
  if (a.desktopElementId && b.desktopElementId && a.desktopElementId === b.desktopElementId) return true;
  return false;
}

function selectNonCollidingBannerCampaign(
  campaigns: RuntimeCampaign[],
  activeFunnelExperience?: ActiveFunnelExperience | null,
): RuntimeCampaign | null {
  const funnelPlacement = normalizePlacement(activeFunnelExperience?.bannerPlacement);
  if (!funnelPlacement) return null;

  return (
    campaigns.find((campaign) => {
      const campaignPlacement = normalizePlacement(campaign.bannerPlacement);
      return !slotsCollide(funnelPlacement, campaignPlacement);
    }) ?? null
  );
}

function isFunnelSpecificCampaign(campaign: RuntimeCampaign): boolean {
  return campaign.scope === 'funnel_specific';
}

export function resolveCampaignSet(
  response: CampaignEvaluationResponse | null | undefined,
  dismissedIds: string[],
  activeFunnelExperience?: ActiveFunnelExperience | null,
): ResolvedCampaignSet {
  const responseMatches = response?.matches;
  const matches = Array.isArray(responseMatches) ? responseMatches : [];
  const fallback = response?.match ? [response.match, ...matches] : matches;
  const seen = new Set<string>();
  const campaigns = fallback
    .map((item) => item.campaign)
    .filter((campaign): campaign is RuntimeCampaign => Boolean(campaign))
    .filter((campaign) => {
      if (dismissedIds.includes(campaign.id)) return false;
      if (seen.has(campaign.id)) return false;
      seen.add(campaign.id);
      return true;
    });

  const bannerCampaigns = campaigns
    .filter((campaign) => campaign.campaignType === 'banner')
    .sort((a, b) => campaignPriorityValue(b) - campaignPriorityValue(a));
  const overlayCampaigns = campaigns
    .filter((campaign) => campaign.campaignType !== 'banner')
    .sort(compareOverlayCampaigns);

  const baseResolved = {
    bannerCampaign: bannerCampaigns[0] ?? null,
    overlayCampaign: overlayCampaigns[0] ?? null,
  };

  switch (activeFunnelExperience?.experienceType) {
    case 'page':
      return {
        bannerCampaign: bannerCampaigns.find(isFunnelSpecificCampaign) ?? null,
        overlayCampaign: overlayCampaigns.find(isFunnelSpecificCampaign) ?? null,
      };
    case 'modal':
    case 'popup':
      return { bannerCampaign: baseResolved.bannerCampaign, overlayCampaign: null };
    case 'banner': {
      const bannerCampaign = selectNonCollidingBannerCampaign(bannerCampaigns, activeFunnelExperience);
      return { bannerCampaign, overlayCampaign: baseResolved.overlayCampaign };
    }
    default:
      return baseResolved;
  }
}
